// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {getSafeConfig} from '@github-ui/issue-create/getSafeConfig'
import {IssueCreateContextProvider} from '@github-ui/issue-create/IssueCreateContext'
import type {PreselectedDataTypes} from '@github-ui/issue-create/IssueCreateDataContext'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {IssueViewerLoading} from '@github-ui/issue-viewer/IssueViewerLoading'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {testIdProps} from '@github-ui/test-id-props'
import {IssueDraftIcon} from '@primer/octicons-react'
import {Banner, Stack} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

import {BROWSER_ANIMATION_DURATION} from '../../../utils/constants'
import type {DraftIssue} from '../content-preview-types'
import {useContentPreview} from '../ContentPreviewContext'
import {FailureState} from '../FailureState'
import filePreviewStyles from '../FilePreview.module.css'
import issuePreviewStyles from '../IssuePreview.module.css'
import {VersionSelector} from '../VersionSelector'
import {CreateIssueButton} from './CreateIssueButton'
import {CreateIssueForm} from './CreateIssueForm'
import styles from './CreateIssuePreview.module.css'
import {ParentSelector} from './ParentSelector'
import {RepositoryPicker} from './RepositoryPicker'
import {TemplatePicker} from './TemplatePicker'
import {type TreeNode, useDraftIssueTreeMap} from './use-draft-issue-tree-map'
import type {SingleIssueCreateError} from './use-issue-creation-handler'
import {useOnChangeCallback} from './use-on-change-callback'
import {useOnFieldChangeCallback} from './use-on-field-change-callback'
import {useRepoQuery} from './use-repo-query'
import {useDraftIssueFormTemplateQuery} from './use-template-query'
import {useTopReposQuery} from './use-top-repos-query'

export const METADATA_FOOTER_BREAKPOINT = 1024

export type CreateIssuePreviewProps = {
  issue: DraftIssue
  isPreviewOpening: boolean
  onClose: () => void
}

export function CreateIssuePreview({isPreviewOpening, issue: draftIssue, onClose}: CreateIssuePreviewProps) {
  const {openItem} = useContentPreview()

  // We know the current node is a DraftIssue;
  // we're fetching it using the tag from our DraftIssue.
  const treeNodes = useDraftIssueTreeMap()
  const currentNode = treeNodes.get(draftIssue.tag) as TreeNode<DraftIssue> | undefined

  // There are rendering issues if we draw the form while animating the workbench opening, so we show loading until the animation is done.
  const [isAnimating, setIsAnimating] = useState(isPreviewOpening)
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => setIsAnimating(false), BROWSER_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isAnimating])

  // Error state for issue creation
  const [creationError, setCreationError] = useState<SingleIssueCreateError | null>(null)
  const errorBanner = useRef<HTMLDivElement>(null)

  const {selectedThreadID} = useChatState()

  // Load repositories
  const {isLoading: loadingTopRepos, data: topRepos} = useTopReposQuery()
  const [owner, repo] = draftIssue.repository?.split('/') ?? []
  const {isLoading: loadingDraftIssueRepo, data: draftIssueRepo} = useRepoQuery({owner, repo})
  const [selectedRepo, setSelectedRepo] = useState(draftIssueRepo)

  useEffect(() => {
    if (draftIssueRepo) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRepo(draftIssueRepo)
    }
  }, [draftIssueRepo])

  const noRepoAccess = useMemo(() => {
    return (
      selectedRepo != null &&
      (!selectedRepo.hasIssuesEnabled ||
        (selectedRepo.viewerInteractionLimitReasonHTML != null &&
          selectedRepo.viewerInteractionLimitReasonHTML?.length > 0))
    )
  }, [selectedRepo])

  // Error callback to handle creation errors
  const onCreateError = useCallback((error: SingleIssueCreateError) => {
    setCreationError(error)
  }, [])

  // Clear error when form changes
  const clearError = useCallback(() => {
    setCreationError(null)
  }, [])

  // Focus error banner when error appears
  useEffect(() => {
    if (creationError && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [creationError])

  const onChange = useOnChangeCallback(draftIssue)
  const onFieldChange = useOnFieldChangeCallback(draftIssue, onChange)

  const containerRef = useRef<HTMLDivElement>(null)
  const renderNarrowView = useRenderNarrowView(containerRef)
  const shouldRenderSubIssueIndicator = currentNode?.parent != null

  const issueFormRef = useRef<IssueFormRef>(null)

  const {isLoading: loadingTemplate, data: templateData} = useDraftIssueFormTemplateQuery({
    owner: draftIssueRepo?.owner.login ?? '',
    repo: draftIssueRepo?.name ?? '',
    filename: draftIssue.template || '',
  })

  const preselectedData: PreselectedDataTypes = {
    repository: draftIssueRepo ?? undefined,
  }

  if (isAnimating || loadingTopRepos || loadingTemplate) {
    return <IssueViewerLoading optionConfig={{useViewportQueries: false}} />
  }

  return (
    <IssueCreateContextProvider
      optionConfig={getSafeConfig({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        storageKeyPrefix: `${draftIssue.id}@${selectedThreadID!}`,
        insidePortal: renderNarrowView,
        disableCopilotAssignmentModal: true,
        isCopilotImmersive: true,
      })}
      preselectedData={preselectedData}
      {...testIdProps('create-issue-preview')}
    >
      <div className={clsx(issuePreviewStyles.container, styles.container)} ref={containerRef}>
        <div className={filePreviewStyles.toolbar}>
          <div className={clsx(filePreviewStyles.toolbarLeft, styles.toolbarLeft)}>
            <div>
              <VersionSelector item={draftIssue} />
            </div>
            {shouldRenderSubIssueIndicator && (
              <div>
                <ParentSelector node={currentNode} onItemSelected={node => openItem(node.item.id, true)} />
              </div>
            )}
          </div>
          <div className={filePreviewStyles.toolbarActions}>
            <CreateIssueButton
              issueTemplate={templateData}
              issueNode={currentNode}
              issueFormRef={issueFormRef}
              noRepoAccess={noRepoAccess}
              onClose={onClose}
              onError={onCreateError}
              onClearError={clearError}
            />
          </div>
        </div>
        <div className={styles.form}>
          <Stack direction={'vertical'} align={'start'} gap={'condensed'} className="py-2">
            <RepositoryPicker
              draftIssue={draftIssue}
              resolvedDraftIssueRepo={draftIssueRepo}
              loadingDraftIssueRepo={loadingDraftIssueRepo}
              topRepos={topRepos || []}
            />
            <TemplatePicker draftIssue={draftIssue} />
          </Stack>
          {creationError && (
            <Banner
              ref={errorBanner}
              hideTitle
              title={creationError.bannerTitle}
              description={<AriaAlert>{creationError.message}</AriaAlert>}
              variant={creationError.bannerVariant}
              role="alert"
              data-testid="create-issue-error-banner"
            />
          )}
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          {noRepoAccess && renderBlankslate(draftIssueRepo!)}
          {!noRepoAccess && (
            <CreateIssueForm
              draftIssue={draftIssue}
              issueNode={currentNode}
              template={templateData ?? undefined}
              issueFormRef={issueFormRef}
              loadingDraftIssueRepo={loadingDraftIssueRepo}
              onChange={onChange}
              onClose={onClose}
              onFieldChange={onFieldChange}
            />
          )}
        </div>
      </div>
    </IssueCreateContextProvider>
  )
}

function renderBlankslate(repository: RepositoryPickerRepository) {
  if (repository && !repository.hasIssuesEnabled) {
    return (
      <FailureState
        title="Issues are disabled"
        description="Issues are disabled on this repository. Please select a different repository above to continue creating your issue."
        icon={IssueDraftIcon}
      />
    )
  }

  if (repository && repository.viewerInteractionLimitReasonHTML) {
    return (
      <FailureState
        title="You cannot create issues in this repository"
        description={repository.viewerInteractionLimitReasonHTML}
        icon={IssueDraftIcon}
      />
    )
  }
}

function useRenderNarrowView<T extends HTMLElement>(elementRef: React.RefObject<T | null>) {
  const subscribe = (notify: () => void) => {
    const resizeObserver = new ResizeObserver(notify)
    resizeObserver.observe(document.documentElement)
    return () => {
      resizeObserver.unobserve(document.documentElement)
      resizeObserver.disconnect()
    }
  }

  const onChange = () => {
    if (!elementRef.current) return true

    return elementRef.current.clientWidth < METADATA_FOOTER_BREAKPOINT
  }

  return useSyncExternalStore(subscribe, onChange)
}
